import { Component, Fragment, h, Host, Prop } from '@stencil/core'
import { State, Watch } from '@stencil/core/internal'
import { AppSharedPropsStore } from 'src/app'
import { PersonAvatar } from 'src/call/avatar/ui'
import { ICallComment } from 'src/call/types'
import { dayjs } from 'src/lib/dayjs'

import { CommentStore } from '../comment'
import { NoteSection } from '../notes/ui'
import { CallStore, hasInternalTeamLabelAccess } from '../state'
import { sortComments } from './utils'

@Component({
  tag: 'page-call-detail-comments-panel',
})
export class CommentsPanel {
  @Prop({ mutable: true }) comments: ICallComment[] = []
  @State() submitting = false
  @State() sortedComments: ICallComment[] = []

  openedThreadIds: Set<number> = new Set()
  text: string

  @Watch('comments')
  onComments() {
    this.sortedComments = this.comments.filter((comment) => !comment.parent_id).sort(sortComments)
  }

  getReplies(parentComment: ICallComment) {
    return this.getNestedReplies(parentComment).sort(sortComments)
  }

  componentWillLoad() {
    this.onComments()
  }

  renderNewComment() {
    return (
      <li class='w-full'>
        <div class='flex flex-row w-full spacing-x-6px'>
          <PersonAvatar
            style={{ fontSize: '15px' }}
            email={AppSharedPropsStore.state.currentUser.email}
            avatarUrl={AppSharedPropsStore.state.currentUser.avatar_url}
          />
          <div class='flex flex-col w-full'>
            <div class='flex flex-row'>
              <span class='text-xsmall text-gray-40'>
                {AppSharedPropsStore.state.currentUser.first_name} {AppSharedPropsStore.state.currentUser.last_name}
              </span>
              <span class='text-xsmall text-gray-100'>&nbsp;&#8226;&nbsp;</span>
              <span class='text-xsmall text-gray-80'>
                {dayjs
                  .duration(CommentStore.state.selectedCue.start_time, 's')
                  .format(CommentStore.state.selectedCue.start_time >= 3600 ? '@H:mm:ss' : '@m:ss')}
              </span>
            </div>
            <page-call-detail-comments-text-area
              key={`page-call-detail-comments-text-area-new-cue-${CommentStore.state.selectedCue.id}}`}
              searchUrl={CallStore.state.actions?.searchMentionablesCommentUrl}
              onSaved={({ detail }) => {
                this.comments.push(detail)
                this.onComments()
              }}
            />
          </div>
        </div>
      </li>
    )
  }

  renderThread(comment: ICallComment) {
    return (
      <page-call-detail-comments-thread
        key={`page-call-detail-comments-thread-${comment.id}`}
        comment={comment}
        replies={this.getReplies(comment)}
        showingReplies={this.openedThreadIds.has(comment.id)}
        onThreadOpened={({ detail }) => {
          if (detail) {
            this.openedThreadIds.add(comment.id)
          } else {
            this.openedThreadIds.delete(comment.id)
          }
        }}
      />
    )
  }

  private getNestedReplies(parentComment: ICallComment) {
    let replies = []
    this.comments.forEach((comment) => {
      if (comment.parent_id === parentComment.id) {
        replies = replies.concat(comment, this.getNestedReplies(comment))
      }
    })
    return replies
  }

  render() {
    return (
      <Host class='w-full'>
        <NoteSection
          label='Comments'
          color='surface-highest'
          renderHeaderContent={() => {
            if (hasInternalTeamLabelAccess(CallStore.state.access)) {
              return (
                <span class='flex items-center ml-2'>
                  <ui-icon icon='lock' class='p-2px mr-1 text-xs text-surface-base bg-info-warn rounded'></ui-icon>
                  <span class='text-xs font-bold tracking-tighter text-info-warn uppercase'>Internal Team Only</span>
                  {this.sortedComments.length > 0 && (
                    <ui-tooltip>If you share this call only folks from your domain can see this content</ui-tooltip>
                  )}
                </span>
              )
            }
          }}
        >
          <Fragment>
            {this.sortedComments.length === 0 && CommentStore.state.selectedCue
              ? this.renderNewComment()
              : this.sortedComments.map((comment, index, arr) => {
                  if (CommentStore.state.selectedCue) {
                    // When there's a comment being added, thread it in it's correct position
                    if (CommentStore.state.selectedCue.start_time < comment.start_time) {
                      // Appears before this comment chronologically
                      return (
                        <Fragment>
                          {this.renderNewComment()}
                          {this.renderThread(comment)}
                        </Fragment>
                      )
                    } else if (index === arr.length - 1) {
                      // Render it after the last comment
                      return (
                        <Fragment>
                          {this.renderThread(comment)}
                          {this.renderNewComment()}
                        </Fragment>
                      )
                    }
                  }

                  return this.renderThread(comment)
                })}
          </Fragment>
        </NoteSection>
      </Host>
    )
  }
}
